import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import { matrixPriority, priorityName, reprioritize, ticketMatrixQuery } from '../api/priorityMatrix'
import { toast } from '../ui/notify'
import { PriorityMatrixFields } from './PriorityMatrixFields'

/**
 * Banda "Urgency x Impact" de pe fisa tichetului (CEC L25).
 *
 * Se deseneaza NUMAI cand furnizorul a aprins matricea in setari; stinsa, fisa
 * arata exact ca pana acum. Arata urgenta, impactul si prioritatea calculata,
 * cu semnul "overridden" cand prioritatea tichetului nu e a matricei.
 *
 * Schimbarea pleaca la `POST tickets/{id}/priority-matrix`, care trece prin
 * aceeasi cale ca butonul "Update": tinta de SLA se recalculeaza, emailul
 * `updated` pleaca, statiile primesc TCH, iar istoricul ("Changes") primeste
 * randurile de urgenta / impact / suprascriere. Lista de prioritati pentru
 * suprascriere apare numai cui are dreptul.
 */
export function TicketPriorityMatrix({ id }: { id: number }) {
  const queryClient = useQueryClient()
  const { data } = useQuery(ticketMatrixQuery(id))

  const [urgency, setUrgency] = useState(0)
  const [impact, setImpact] = useState(0)
  const [override, setOverride] = useState(0)

  useEffect(() => {
    setUrgency(data?.current?.urgency_id ?? 0)
    setImpact(data?.current?.impact_id ?? 0)
    setOverride(data?.current?.overridden ? (data?.ticketPriority ?? 0) : 0)
  }, [data?.current?.urgency_id, data?.current?.impact_id, data?.current?.overridden, data?.ticketPriority])

  const save = useMutation({
    mutationFn: () => reprioritize(id, urgency, impact, override > 0 ? override : null),
    onSuccess: (answer) => {
      toast(
        answer.overridden
          ? `Priority set to ${priorityName(data?.priorities ?? [], answer.priority)} (overridden)`
          : `Priority set to ${priorityName(data?.priorities ?? [], answer.priority)}`,
        'Priority',
      )
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'The priority could not be saved.', 'Priority', 'error'),
  })

  /* Stinsa, fara drept, sau placa inca n-are capul: fisa ramane cum era. */
  if (!data || !data.enabled) return null

  const computed = matrixPriority(data, urgency, impact)
  const unchanged =
    urgency === (data.current?.urgency_id ?? 0) &&
    impact === (data.current?.impact_id ?? 0) &&
    override === (data.current?.overridden ? data.ticketPriority : 0)

  return (
    <div className="panel panel-default tk-priority-matrix-band" style={{ margin: '0 0 10px' }}>
      <div className="panel-heading">
        <strong>Urgency x Impact</strong>{' '}
        <span className="text-muted">
          - priority now: <strong>{priorityName(data.priorities, data.ticketPriority) || '-'}</strong>
          {data.current?.overridden && <span className="label label-warning tk-overridden"> overridden</span>}
        </span>
      </div>
      <div className="panel-body form-horizontal">
        <PriorityMatrixFields
          matrix={data}
          urgency={urgency}
          impact={impact}
          onUrgency={setUrgency}
          onImpact={setImpact}
          idPrefix={`ticket-${id}`}
          disabled={!data.editable || save.isPending}
        />

        {data.canOverride && data.editable && (
          <div className="form-group">
            <label htmlFor={`ticket-${id}-override`} className="col-sm-3 control-label">
              Override priority
            </label>
            <div className="col-sm-9">
              <select
                id={`ticket-${id}-override`}
                className="form-control"
                value={override}
                disabled={save.isPending}
                onChange={(e) => setOverride(Number(e.target.value))}
              >
                <option value={0}>- use the matrix{computed !== null ? ` (${priorityName(data.priorities, computed)})` : ''} -</option>
                {data.priorities.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {data.editable && (
          <div className="form-group">
            <div className="col-sm-offset-3 col-sm-9">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={unchanged || urgency < 1 || impact < 1 || save.isPending}
                onClick={() => save.mutate()}
              >
                <i className="fas fa-check" aria-hidden="true"></i> Save priority
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
